import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import HoldButton from '../../Shared/HoldButton';
import styles from './CraftingDistrict.module.css';
import CraftingProgress from './CraftingProgress';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { getIngredientIconSrc, getItemImageSrc, handleImgError } from '../../../utils/itemUtils';

interface Item {
  id: number;
  name: string;
  itemId?: number;
  quantity?: number;
}

interface Profession {
  id: number;
  name: string;
  maxLevel: number;
  currentLevel: number;
  blueprints?: Blueprint[];
}

type BlueprintStatus = 'available' | 'researching' | 'unlocked' | 'preview' | 'scroll_locked';

interface Blueprint {
  id: number;
  name: string;
  description: string;
  itemId: number;
  img?: string;
  components: string | { itemId: number; name: string; quantity: number }[];
  requiredLevel: number;
  roundsToCraft: number;
  researchRounds: number;
  researchComponents: string | { itemId: number; name: string; quantity: number }[] | null;
  blueprintStatus: BlueprintStatus;
  remainingResearchRounds: number | null;
  totalResearchRounds: number;
  userBlueprintId: number | null;
  itemCategory?: string | null;
  itemImg?: string | null;
  weaponType?: string | null;
  itemSlot?: string | null;
  accessoryType?: string | null;
  runeColor?: string | null;
  isFineBlueprint?: boolean;
  requiresScroll?: boolean;
}

interface MercenaryData {
  id: number;
  professionSkillPoints: number;
  rounds: number;
}

const CraftingDistrict: React.FC = () => {
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [selectedProfession, setSelectedProfession] = useState<Profession | null>(null);
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(null);
  const [mercenary, setMercenary] = useState<MercenaryData | null>(null);
  const [forgeLevel, setForgeLevel] = useState<number>(0);
  const { mercenaryId, mercenary: liveMercenary } = useMercenary();
  const [blueprintQuantities, setBlueprintQuantities] = useState<{ [blueprintId: number]: number }>({});
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [subFilter, setSubFilter] = useState<string | null>(null);
  const [collapsedLevels, setCollapsedLevels] = useState<Set<number>>(new Set());
  const [view, setView] = useState<'crafting' | 'progress'>('crafting');
  const [progressTab, setProgressTab] = useState<'crafting' | 'research'>('research');
  const [inventoryItems, setInventoryItems] = useState<Item[]>([]);
  const [researchRounds, setResearchRounds] = useState<{ [userBlueprintId: number]: number }>({});
  const [mobilePanelView, setMobilePanelView] = useState<'list' | 'detail'>('list');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);

  useEffect(() => {
    fetchMercenaryAndProfessions().then((profs) => {
      if (profs.length > 0) {
        const levelled = profs.filter((p) => p.currentLevel > 0);
        const autoSelect = levelled.length > 0
          ? levelled.reduce((best, p) => p.currentLevel > best.currentLevel ? p : best, levelled[0])
          : profs[0];
        setSelectedProfession(autoSelect);
      }
    });
    fetchInventory();
  }, [mercenaryId]);

  const handleBackToWorkshop = async () => {
    setView('crafting');
    const updatedProfessions = await fetchMercenaryAndProfessions();
    fetchInventory();
    if (selectedProfession && selectedBlueprint) {
      const updatedProf = updatedProfessions.find((p) => p.id === selectedProfession.id);
      if (updatedProf) {
        setSelectedProfession(updatedProf);
        const updatedBp = updatedProf.blueprints?.find((b) => b.id === selectedBlueprint.id);
        if (updatedBp) setSelectedBlueprint(updatedBp);
      }
    }
  };

  const fetchMercenaryAndProfessions = async (): Promise<Profession[]> => {
    try {
      const [{ data: mercenaryData }, { data: cityStatus }] = await Promise.all([
        axios.get(`/api/mercenary/${mercenaryId}`, { withCredentials: true }),
        axios.get('/api/city-buildings/status', { withCredentials: true }),
      ]);
      setMercenary(mercenaryData);
      const forgeBuilding = (cityStatus as any[]).find((b: any) => b.buildingType === 'runeForge');
      setForgeLevel(forgeBuilding ? forgeBuilding.currentLevel : 0);

      const { data: professionData } = await axios.get(`/api/craft/professions/${mercenaryData.id}`, { withCredentials: true });

      // Blueprints already come as flat arrays with blueprintStatus from the new API
      const sorted = sortProfessions(professionData);
      setProfessions(sorted);
      return sorted;
    } catch (error) {
      displayToast('Error fetching profession data', 'error');
      return [];
    }
  };

  const fetchInventory = async () => {
    try {
      if (!mercenaryId) return;
      const response = await axios.get(`/api/inventory/${mercenaryId}`, { withCredentials: true });
      setInventoryItems(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getInventoryCount = (targetItemId: number): number => {
    return inventoryItems.reduce((total, item) => {
      const itemId = item.itemId ?? item.id;
      if (itemId === targetItemId) {
        return total + (item.quantity || 1);
      }
      return total;
    }, 0);
  };

  const sortProfessions = (professions: Profession[]): Profession[] => {
    const order = ['Weapon Smithing', 'Armor Smithing', 'Jeweler', 'Alchemy', 'Rune Carving'];
    return professions.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  };

  const handleProfessionSelect = (profession: Profession) => {
    setSelectedProfession(profession);
    setSelectedBlueprint(null);
    setCategoryFilter(null);
    setSubFilter(null);
    setCollapsedLevels(new Set());
    setMobilePanelView('list');
    setSidebarOpen(false);
  };

  const handleQuantityChange = (blueprintId: number, change: number) => {
    setBlueprintQuantities((prev) => {
      const current = prev[blueprintId] || 1;
      const newValue = Math.max(1, current + change);
      return { ...prev, [blueprintId]: newValue };
    });
  };

  const handleQuantityInput = (blueprintId: number, value: number) => {
    setBlueprintQuantities((prev) => ({
      ...prev,
      [blueprintId]: Math.max(0, value),
    }));
  };

  const handleStartCraft = async (blueprint: Blueprint, quantity: number) => {
    try {
      await axios.post('/api/craft/start-craft', { mercenaryId: mercenary?.id, blueprintId: blueprint.id, quantity }, { withCredentials: true });
      displayToast('Crafting project started!', 'success');
      fetchInventory();
      setProgressTab('crafting');
      setView('progress');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to start crafting project.', 'error');
    }
  };

  const handleStartResearch = async (blueprint: Blueprint) => {
    try {
      await axios.post('/api/craft/start-research', { mercenaryId: mercenary?.id, blueprintId: blueprint.id }, { withCredentials: true });
      displayToast('Research started!', 'success');
      const updatedProfessions = await fetchMercenaryAndProfessions();
      fetchInventory();

      // Re-select the blueprint so the worktable shows the researching view
      if (selectedProfession) {
        const updatedProf = updatedProfessions.find(p => p.id === selectedProfession.id);
        if (updatedProf) {
          setSelectedProfession(updatedProf);
          const updatedBp = updatedProf.blueprints?.find(b => b.id === blueprint.id);
          if (updatedBp) setSelectedBlueprint(updatedBp);
        }
      }
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to start research.', 'error');
    }
  };

  const handleWorkOnResearch = async (userBlueprintId: number) => {
    const rounds = researchRounds[userBlueprintId] || 1;
    try {
      const { data } = await axios.post(`/api/craft/research/${userBlueprintId}/work`, { rounds, mercenaryId: mercenary?.id }, { withCredentials: true });
      if (data.completed) {
        displayToast('Research complete! Blueprint unlocked.', 'success');
      } else {
        displayToast('Research progress updated.', 'success');
      }
      // Clear the rounds input after studying
      setResearchRounds((prev) => { const next = { ...prev }; delete next[userBlueprintId]; return next; });
      const updatedProfessions = await fetchMercenaryAndProfessions();

      // Re-select the updated blueprint so the worktable reflects new state
      if (selectedProfession && selectedBlueprint) {
        const updatedProf = updatedProfessions.find(p => p.id === selectedProfession.id);
        if (updatedProf) {
          setSelectedProfession(updatedProf);
          const updatedBp = updatedProf.blueprints?.find(b => b.id === selectedBlueprint.id);
          if (updatedBp) setSelectedBlueprint(updatedBp);
        }
      }
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to work on research.', 'error');
    }
  };

  const handleResearchRoundChange = (userBlueprintId: number, value: number) => {
    setResearchRounds((prev) => ({ ...prev, [userBlueprintId]: Math.max(0, value) }));
  };

  const handleCancelResearch = async (userBlueprintId: number) => {
    try {
      await axios.delete(`/api/craft/research/${userBlueprintId}/cancel`, {
        data: { mercenaryId: mercenary?.id },
        withCredentials: true,
      });
      setConfirmCancelId(null);
      displayToast('Research cancelled.', 'success');
      const updatedProfessions = await fetchMercenaryAndProfessions();
      // Deselect or refresh the blueprint so it shows 'available' again
      if (selectedProfession && selectedBlueprint) {
        const updatedProf = updatedProfessions.find(p => p.id === selectedProfession.id);
        if (updatedProf) {
          setSelectedProfession(updatedProf);
          const updatedBp = updatedProf.blueprints?.find(b => b.id === selectedBlueprint.id);
          if (updatedBp) setSelectedBlueprint(updatedBp);
        }
      }
    } catch (err: any) {
      setConfirmCancelId(null);
      displayToast(err.response?.data?.message || 'Failed to cancel research.', 'error');
    }
  };

  const handleLevelUp = async () => {
    if (!selectedProfession || !mercenary || mercenary.professionSkillPoints === 0) return;

    try {
      await axios.post('/api/craft/professions/level-up', { mercenaryId: mercenary.id, professionId: selectedProfession.id }, { withCredentials: true });
      displayToast('Profession leveled up successfully!', 'success');
      const updatedProfessions = await fetchMercenaryAndProfessions();
      // Re-select the updated profession so blueprints refresh immediately without F5
      const updatedProf = updatedProfessions.find(p => p.id === selectedProfession.id);
      if (updatedProf) setSelectedProfession(updatedProf);
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to level up profession.', 'error');
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  // Group blueprints by level for display
  const getBlueprintsByLevel = (profession: Profession): { level: number; blueprints: Blueprint[]; isPreview: boolean }[] => {
    if (!profession.blueprints || !Array.isArray(profession.blueprints)) return [];

    const grouped: { [level: number]: Blueprint[] } = {};
    for (const bp of profession.blueprints) {
      if (!grouped[bp.requiredLevel]) grouped[bp.requiredLevel] = [];
      grouped[bp.requiredLevel].push(bp);
    }

    const levels = Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b);

    return levels.map(level => ({
      level,
      blueprints: grouped[level].sort((a, b) => a.name.localeCompare(b.name)),
      isPreview: level > profession.currentLevel,
    }));
  };

  const getStatusIcon = (status: BlueprintStatus) => {
    switch (status) {
      case 'unlocked': return '✅';
      case 'researching': return '🔬';
      case 'available': return '📖';
      case 'preview': return '🔒';
      default: return '';
    }
  };

  const getStatusLabel = (status: BlueprintStatus) => {
    switch (status) {
      case 'unlocked': return 'Ready to Craft';
      case 'researching': return 'Researching...';
      case 'available': return 'Available to Study';
      case 'preview': return 'Locked';
      default: return '';
    }
  };

  const renderWorktable = () => {
    if (!selectedBlueprint || !selectedProfession) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>⚔️</div>
          <h3>Select a Blueprint</h3>
          <p>Choose an item from the list to view requirements.</p>
        </div>
      );
    }

    const bp = selectedBlueprint;

    // === PREVIEW: Locked blueprint (next level) ===
    if (bp.blueprintStatus === 'preview') {
      return (
        <div className={styles.worktableContent}>
          <div className={styles.blueprintHeader}>
            <h2 className={styles.blueprintTitle}>{bp.name}</h2>
            <div className={styles.resultIconWrapper}>
              <Tooltip itemId={bp.itemId}>
                <img
                  src={bp.itemImg ? getItemImageSrc({ img: bp.itemImg, itemCategory: bp.itemCategory }) : `/img/icons/${selectedProfession.name.toLowerCase().replace(/\s+/g, '')}.png`}
                  alt={bp.name}
                  className={styles.resultIconLarge}
                  style={{ filter: 'grayscale(80%) brightness(0.6)' }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                  }}
                />
              </Tooltip>
            </div>
            <p className={styles.blueprintDescription}>{bp.description || 'A craftable item.'}</p>
          </div>
          <div className={styles.lockedNotice}>
            <span>🔒</span>
            <p>Requires Level {bp.requiredLevel} in {selectedProfession.name} to research.</p>
          </div>
        </div>
      );
    }

    // === AVAILABLE: Can start researching ===
    if (bp.blueprintStatus === 'available') {
      const researchComps: { itemId: number; name: string; quantity: number }[] = bp.researchComponents
        ? (typeof bp.researchComponents === 'string' ? JSON.parse(bp.researchComponents) : bp.researchComponents)
        : [];
      const hasResearchMaterials = researchComps.every(rc => getInventoryCount(rc.itemId) >= rc.quantity);

      return (
        <div className={styles.worktableContent}>
          <div className={styles.blueprintHeader}>
            <h2 className={styles.blueprintTitle}>{bp.name}</h2>
            <div className={styles.resultIconWrapper}>
              <Tooltip itemId={bp.itemId}>
                <img
                  src={bp.itemImg ? getItemImageSrc({ img: bp.itemImg, itemCategory: bp.itemCategory }) : `/img/icons/${selectedProfession.name.toLowerCase().replace(/\s+/g, '')}.png`}
                  alt={bp.name}
                  className={styles.resultIconLarge}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                  }}
                />
              </Tooltip>
            </div>
            <p className={styles.blueprintDescription}>{bp.description || 'A craftable item.'}</p>
          </div>

          <div className={styles.ingredientsSection}>
            <div className={styles.sectionTitle}>Research Cost</div>
            <div className={styles.timeEstimate}>
              Study Time: {bp.totalResearchRounds} Rounds
            </div>
            {researchComps.length > 0 && (
              <div className={styles.ingredientsGrid}>
                {researchComps.map((rc, idx) => {
                  const owned = getInventoryCount(rc.itemId);
                  const isSufficient = owned >= rc.quantity;
                  return (
                    <Tooltip key={idx} itemId={rc.itemId}>
                      <div className={`${styles.ingredientSlot} ${isSufficient ? styles.sufficient : styles.insufficient}`}>
                        <img
                          src={getIngredientIconSrc(rc.name)}
                          alt={rc.name}
                          className={styles.ingredientIcon}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                          }}
                        />
                        <div className={`${styles.ingredientQty} ${isSufficient ? styles.sufficient : styles.insufficient}`}>
                          {owned} / {rc.quantity}
                        </div>
                        <div className={styles.ingredientName}>{rc.name}</div>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>

          {/* Crafting Materials Preview */}
          {(() => {
            const craftComps: { itemId: number; name: string; quantity: number }[] = bp.components
              ? (typeof bp.components === 'string' ? JSON.parse(bp.components) : bp.components)
              : [];
            if (craftComps.length === 0) return null;
            return (
              <div className={styles.ingredientsSection}>
                <div className={styles.sectionTitle}>Crafting Materials</div>
                <div className={styles.timeEstimate}>
                  Crafting Time: {bp.roundsToCraft} Rounds
                </div>
                <div className={styles.ingredientsGrid}>
                  {craftComps.map((cc, idx) => (
                    <Tooltip key={idx} itemId={cc.itemId}>
                      <div className={styles.ingredientSlot}>
                        <img
                          src={getIngredientIconSrc(cc.name)}
                          alt={cc.name}
                          className={styles.ingredientIcon}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                          }}
                        />
                        <div className={styles.ingredientQty}>{cc.quantity}</div>
                        <div className={styles.ingredientName}>{cc.name}</div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className={styles.craftingControls}>
            <button
              onClick={() => handleStartResearch(bp)}
              disabled={!hasResearchMaterials}
              className={styles.craftButton}
            >
              {hasResearchMaterials ? 'Begin Research' : 'Missing Research Materials'}
            </button>
          </div>
        </div>
      );
    }

    // === RESEARCHING: Work on research ===
    if (bp.blueprintStatus === 'researching' && bp.userBlueprintId) {
      const remaining = bp.remainingResearchRounds ?? 0;
      const maxRounds = Math.min(remaining, liveMercenary?.rounds ?? mercenary?.rounds ?? remaining);
      const total = bp.totalResearchRounds || 1;
      const spent = total - remaining;
      const progressPct = Math.min(100, Math.round((spent / total) * 100));
      const roundsToWork = researchRounds[bp.userBlueprintId] || 1;

      return (
        <div className={styles.worktableContent}>
          <div className={styles.blueprintHeader}>
            <h2 className={styles.blueprintTitle}>{bp.name}</h2>
            <div className={styles.resultIconWrapper}>
              <Tooltip itemId={bp.itemId}>
                <img
                  src={bp.itemImg ? getItemImageSrc({ img: bp.itemImg, itemCategory: bp.itemCategory }) : `/img/icons/${selectedProfession.name.toLowerCase().replace(/\s+/g, '')}.png`}
                  alt={bp.name}
                  className={styles.resultIconLarge}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                  }}
                />
              </Tooltip>
            </div>
            <p className={styles.blueprintDescription}>{bp.description || 'A craftable item.'}</p>
          </div>

          <div className={styles.ingredientsSection}>
            <div className={styles.sectionTitle}>Research Progress</div>
            <div className={styles.progressBarContainer}>
              <div className={styles.progressBar} style={{ width: `${progressPct}%` }} />
              <span className={styles.progressText}>{spent} / {total} rounds ({progressPct}%)</span>
            </div>
          </div>

          {/* Crafting Materials Preview */}
          {(() => {
            const craftComps: { itemId: number; name: string; quantity: number }[] = bp.components
              ? (typeof bp.components === 'string' ? JSON.parse(bp.components) : bp.components)
              : [];
            if (craftComps.length === 0) return null;
            return (
              <div className={styles.ingredientsSection}>
                <div className={styles.sectionTitle}>Crafting Materials</div>
                <div className={styles.timeEstimate}>
                  Crafting Time: {bp.roundsToCraft} Rounds
                </div>
                <div className={styles.ingredientsGrid}>
                  {craftComps.map((cc, idx) => (
                    <Tooltip key={idx} itemId={cc.itemId}>
                      <div className={styles.ingredientSlot}>
                        <img
                          src={getIngredientIconSrc(cc.name)}
                          alt={cc.name}
                          className={styles.ingredientIcon}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                          }}
                        />
                        <div className={styles.ingredientQty}>{cc.quantity}</div>
                        <div className={styles.ingredientName}>{cc.name}</div>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className={styles.craftingControls}>
            <div className={styles.quantityControl}>
              <HoldButton
                onClick={() => setResearchRounds((prev) => ({ ...prev, [bp.userBlueprintId!]: Math.max(1, (prev[bp.userBlueprintId!] || 1) - 1) }))}
                className={styles.qtyButton}
              >-</HoldButton>
              <input
                type="number"
                value={researchRounds[bp.userBlueprintId!] || ''}
                placeholder="1"
                min={1}
                max={maxRounds}
                onChange={(e) => handleResearchRoundChange(bp.userBlueprintId!, e.target.value === '' ? 0 : Math.min(maxRounds, parseInt(e.target.value) || 0))}
                className={styles.qtyInput}
              />
              <HoldButton
                onClick={() => setResearchRounds((prev) => ({ ...prev, [bp.userBlueprintId!]: Math.min(maxRounds, (prev[bp.userBlueprintId!] || 1) + 1) }))}
                className={styles.qtyButton}
              >+</HoldButton>
            </div>
            <button
              onClick={() => handleWorkOnResearch(bp.userBlueprintId!)}
              className={styles.craftButton}
            >
              Study<br />
              <span style={{ fontSize: '0.75em', opacity: 0.7 }}>({roundsToWork} rounds)</span>
            </button>
            {confirmCancelId === bp.userBlueprintId ? (
              <>
                <button onClick={() => handleCancelResearch(bp.userBlueprintId!)} className={styles.cancelButton}>
                  Confirm Cancel
                </button>
                <button onClick={() => setConfirmCancelId(null)} className={styles.craftButton}>
                  No
                </button>
              </>
            ) : (
              <button onClick={() => setConfirmCancelId(bp.userBlueprintId!)} className={styles.cancelButton}>
                Cancel Research
              </button>
            )}
          </div>
        </div>
      );
    }

    // === UNLOCKED: Ready to craft ===
    if (bp.blueprintStatus === 'unlocked') {
      const { components } = bp;
      const requirements: { itemId: number; name: string; quantity: number; img?: string }[] =
        typeof components === 'string' ? JSON.parse(components) : components;

      const quantity = blueprintQuantities[bp.id] || 1;
      const totalRounds = bp.roundsToCraft * quantity;
      const canCraft = requirements.every(req => getInventoryCount(req.itemId) >= (req.quantity * quantity));

      return (
        <div className={styles.worktableContent}>
          <div className={styles.blueprintHeader}>
            <h2 className={styles.blueprintTitle}>{bp.name}</h2>
            <div className={styles.resultIconWrapper}>
              <Tooltip itemId={bp.itemId}>
                <img
                  src={bp.itemImg ? getItemImageSrc({ img: bp.itemImg, itemCategory: bp.itemCategory }) : `/img/icons/${selectedProfession.name.toLowerCase().replace(/\s+/g, '')}.png`}
                  alt={bp.name}
                  className={styles.resultIconLarge}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                  }}
                />
              </Tooltip>
            </div>
            <p className={styles.blueprintDescription}>{bp.description || 'A craftable item.'}</p>
          </div>

          {bp.itemCategory === 'Rune' && (
            <div className={styles.qualityHint}>
              <div className={styles.sectionTitle}>Quality Tiers</div>
              <p className={styles.qualityHintText}>
                {bp.isFineBlueprint || bp.requiresScroll
                  ? 'This rune bypasses Flawed quality and can be crafted as Fine, Excellent, or Perfect.'
                  : 'This rune can be crafted in Flawed, Fine, Excellent, or Perfect quality. Upgrade your Rune Forge and raise your luck to improve your odds.'}
              </p>
              <div className={styles.qualityTierChips}>
                {!(bp.isFineBlueprint || bp.requiresScroll) && <span className={styles.tierChip} data-tier="flawed">Flawed</span>}
                <span className={styles.tierChip} data-tier="fine">Fine</span>
                <span className={styles.tierChip} data-tier="excellent">Excellent</span>
                <span className={styles.tierChip} data-tier="perfect">Perfect</span>
              </div>
            </div>
          )}

          <div className={styles.ingredientsSection}>
            <div className={styles.sectionTitle}>Required Materials</div>
            <div className={styles.ingredientsGrid}>
              {requirements.map((req, idx) => {
                const requiredTotal = req.quantity * quantity;
                const owned = getInventoryCount(req.itemId);
                const isSufficient = owned >= requiredTotal;

                return (
                  <Tooltip key={idx} itemId={req.itemId}>
                    <div className={`${styles.ingredientSlot} ${isSufficient ? styles.sufficient : styles.insufficient}`}>
                      <img
                        src={req.img || getIngredientIconSrc(req.name)}
                        alt={req.name}
                        className={styles.ingredientIcon}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.onerror = null;
                          if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                        }}
                      />
                      <div className={`${styles.ingredientQty} ${isSufficient ? styles.sufficient : styles.insufficient}`}>
                        {owned} / {requiredTotal}
                      </div>
                      <div className={styles.ingredientName}>{req.name}</div>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          <div className={styles.craftingControls}>
            <div className={styles.quantityControl}>
              <HoldButton
                onClick={() => setBlueprintQuantities((prev) => ({ ...prev, [bp.id]: Math.max(1, (prev[bp.id] || 1) - 1) }))}
                className={styles.qtyButton}
              >-</HoldButton>
              <input
                type="number"
                value={blueprintQuantities[bp.id] || ''}
                placeholder="1"
                onChange={(e) => handleQuantityInput(bp.id, e.target.value === '' ? 0 : parseInt(e.target.value) || 0)}
                className={styles.qtyInput}
              />
              <HoldButton
                onClick={() => setBlueprintQuantities((prev) => ({ ...prev, [bp.id]: (prev[bp.id] || 1) + 1 }))}
                className={styles.qtyButton}
              >+</HoldButton>
            </div>

            <div className={styles.timeEstimate}>
              Estimated Time: {totalRounds} Rounds
            </div>

            <button
              onClick={() => handleStartCraft(bp, quantity)}
              disabled={!canCraft}
              className={styles.craftButton}
            >
              {canCraft ? 'Start Crafting' : 'Missing Materials'}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.craftingDistrictContainer}>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      <div className={styles.headerSection}>
        <h1 className={styles.craftingTitle}>
          {selectedProfession ? selectedProfession.name : 'Crafting District'}
        </h1>
        <p className={styles.subTitle}>
          {selectedProfession
            ? `Level ${selectedProfession.currentLevel} / ${selectedProfession.maxLevel}`
            : 'Master the Arts of Creation'}
        </p>
      </div>

      {view === 'progress' ? (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: '15px' }}>
          <button onClick={handleBackToWorkshop} className={styles.backButton}>
            Back to Workshop
          </button>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <CraftingProgress mercenary={mercenary!} defaultTab={progressTab} />
          </div>
        </div>
      ) : (
        <div className={styles.contentWrapper}>
          {/* Left Sidebar: Profession Toggle */}
          <div className={styles.sidebar}>
            <button onClick={view === 'crafting' ? () => setView('progress') : handleBackToWorkshop} className={styles.sidebarActionButton}>
              {view === 'crafting' ? 'View Ongoing Projects' : 'Back to Workshop'}
            </button>

            {/* Collapsed profession selector header — mobile only (hidden on desktop via CSS) */}
            <div className={styles.professionSelector} onClick={() => setSidebarOpen((o) => !o)}>
              <div>
                <div className={styles.professionName}>{selectedProfession?.name ?? 'Select Profession'}</div>
                {selectedProfession && (
                  <div className={styles.professionLevel}>Lvl {selectedProfession.currentLevel} / {selectedProfession.maxLevel}</div>
                )}
              </div>
              <span className={styles.levelHeaderChevron}>{sidebarOpen ? '▼' : '▶'}</span>
            </div>

            {/* Profession list: always visible on desktop, toggle-controlled on mobile */}
            <div className={`${styles.professionList} ${sidebarOpen ? styles.professionListOpen : ''}`}>
              <div className={styles.recipeListHeader}>Professions</div>
              {professions.map((prof) => {
                  const isRuneCarving = prof.name === 'Rune Carving';
                  const isLocked = isRuneCarving && forgeLevel === 0;
                  return (
                    <div
                      key={prof.id}
                      className={`${styles.professionCard} ${selectedProfession?.id === prof.id && !isLocked ? styles.active : ''} ${isLocked ? styles.lockedProfession || '' : ''}`}
                      onClick={() => !isLocked && handleProfessionSelect(prof)}
                      title={isLocked ? 'Requires the Rune Forge to be constructed' : undefined}
                      style={isLocked ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    >
                      <h3 className={styles.professionName}>{prof.name}</h3>
                      {isLocked
                        ? <p className={styles.professionLevel} style={{ color: '#f0a060' }}>🔒Forge</p>
                        : <p className={styles.professionLevel}>Lvl {prof.currentLevel}</p>
                      }
                    </div>
                  );
              })}
            </div>

            <div className={styles.sidebarFooter}>
              <p style={{ fontSize: '0.8rem', color: '#888', textAlign: 'center' }}>
                Skill Points: {mercenary?.professionSkillPoints ?? 0}
              </p>
            </div>
          </div>

          {/* Main Area */}
          <div className={`${styles.workshopArea} ${mobilePanelView === 'list' ? styles.mobilePanelList : styles.mobilePanelDetail}`}>
            {selectedProfession ? (
              <>
                {/* Mobile panel toggle – hidden on desktop via CSS */}
                <div className={styles.mobileTabs}>
                  <button
                    className={`${styles.mobileTab} ${mobilePanelView === 'list' ? styles.mobileTabActive : ''}`}
                    onClick={() => setMobilePanelView('list')}
                  >
                    Blueprints
                  </button>
                  <button
                    className={`${styles.mobileTab} ${mobilePanelView === 'detail' ? styles.mobileTabActive : ''}`}
                    onClick={() => setMobilePanelView('detail')}
                    disabled={!selectedBlueprint}
                  >
                    {selectedBlueprint ? selectedBlueprint.name : 'Details'}
                  </button>
                </div>

                {/* Middle: Blueprint List grouped by level */}
                <div className={styles.recipeListContainer}>
                  <div className={styles.recipeListHeader}>Blueprints</div>
                  {(() => {
                    const allBps = selectedProfession.blueprints || [];
                    const cats = [...new Set(allBps.map(bp => bp.itemCategory).filter(Boolean))].sort() as string[];
                    // Determine the effective category for sub-filtering
                    const effectiveCategory = categoryFilter ?? (cats.length === 1 ? cats[0] : null);
                    // Map category → the blueprint field used for sub-filtering
                    const subFilterField: Record<string, keyof Blueprint> = {
                      Weapon: 'weaponType',
                      Armor: 'itemSlot',
                      Accessory: 'accessoryType',
                      Rune: 'runeColor',
                    };
                    const activeSubField = effectiveCategory ? subFilterField[effectiveCategory] : null;
                    const subOpts = activeSubField
                      ? ([...new Set(allBps
                          .filter(bp => !categoryFilter || bp.itemCategory === categoryFilter)
                          .map(bp => bp[activeSubField] as string | null | undefined)
                          .filter(Boolean))] as string[]).sort()
                      : [];
                    // Capitalise rune color labels
                    const subLabel = (v: string) =>
                      effectiveCategory === 'Rune' ? v.charAt(0).toUpperCase() + v.slice(1) : v;
                    return (
                      <>
                        {cats.length >= 2 && (
                          <div className={styles.categoryFilter}>
                            <button
                              className={`${styles.filterBtn} ${categoryFilter === null ? styles.filterBtnActive : ''}`}
                              onClick={() => { setCategoryFilter(null); setSubFilter(null); }}
                            >All</button>
                            {cats.map(cat => (
                              <button
                                key={cat}
                                className={`${styles.filterBtn} ${categoryFilter === cat ? styles.filterBtnActive : ''}`}
                                onClick={() => { setCategoryFilter(cat); setSubFilter(null); }}
                              >{cat}</button>
                            ))}
                          </div>
                        )}
                        {subOpts.length >= 2 && (
                          <div className={`${styles.categoryFilter} ${styles.subFilter}`}>
                            <button
                              className={`${styles.filterBtn} ${subFilter === null ? styles.filterBtnActive : ''}`}
                              onClick={() => setSubFilter(null)}
                            >All</button>
                            {subOpts.map(opt => (
                              <button
                                key={opt}
                                className={`${styles.filterBtn} ${subFilter === opt ? styles.filterBtnActive : ''}`}
                                onClick={() => setSubFilter(opt)}
                              >{subLabel(opt)}</button>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div className={styles.recipeList}>
                    {selectedProfession.currentLevel === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>
                        <p>Learn this profession to view available blueprints.</p>
                      </div>
                    ) : (
                      getBlueprintsByLevel(selectedProfession)
                        .map(({ level, blueprints, isPreview }) => {
                          let filtered = (categoryFilter
                            ? blueprints.filter(bp => bp.itemCategory === categoryFilter)
                            : blueprints).filter(bp => bp.blueprintStatus !== 'scroll_locked');
                          if (subFilter) {
                            filtered = filtered.filter(bp =>
                              bp.weaponType === subFilter ||
                              bp.itemSlot === subFilter ||
                              bp.accessoryType === subFilter ||
                              bp.runeColor === subFilter
                            );
                          }
                          if (filtered.length === 0) return null;
                          return { level, blueprints: filtered, isPreview };
                        })
                        .filter((item): item is NonNullable<typeof item> => item !== null)
                        .map(({ level, blueprints, isPreview }) => {
                          const isCollapsed = collapsedLevels.has(level);
                          const toggleCollapse = () => setCollapsedLevels(prev => {
                            const next = new Set(prev);
                            if (next.has(level)) next.delete(level); else next.add(level);
                            return next;
                          });
                          return (
                        <div key={level}>
                          <div
                            className={`${styles.levelHeader} ${isCollapsed ? styles.levelHeaderCollapsed : ''}`}
                            style={isPreview ? { opacity: 0.5 } : undefined}
                            onClick={toggleCollapse}
                          >
                            <span>Level {level} {isPreview && '(Preview)'}</span>
                            <span className={styles.levelHeaderChevron}>{isCollapsed ? '▶' : '▼'}</span>
                          </div>
                          {!isCollapsed && blueprints.map((bp) => (
                            <div
                              key={bp.id}
                              className={`${styles.recipeItem} ${selectedBlueprint?.id === bp.id ? styles.active : ''} ${isPreview ? styles.previewItem : ''}`}
                              onClick={() => { setSelectedBlueprint(bp); if (!isPreview) setMobilePanelView('detail'); }}
                              style={isPreview ? { opacity: 0.5 } : undefined}
                            >
                              <Tooltip itemId={bp.itemId}>
                              <img
                                src={bp.itemImg ? getItemImageSrc({ img: bp.itemImg, itemCategory: bp.itemCategory }) : `/img/icons/${selectedProfession.name.toLowerCase().replace(/\s+/g, '')}.png`}
                                alt={bp.name}
                                className={styles.recipeIcon}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  if (!target.src.includes('no_img.png')) target.src = '/img/no_img.png';
                                }}
                              />
                              </Tooltip>
                              <div className={styles.recipeInfo}>
                                <span className={styles.recipeName}>{bp.name}</span>
                                <span className={styles.recipeLevel}>
                                  {getStatusIcon(bp.blueprintStatus)} {getStatusLabel(bp.blueprintStatus)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        );
                        })
                    )}
                  </div>
                  {mercenary && mercenary.professionSkillPoints > 0 && selectedProfession.currentLevel < selectedProfession.maxLevel && (
                    <div style={{ padding: '10px', display: 'flex', justifyContent: 'center' }}>
                      <button onClick={handleLevelUp} className={styles.qtyButton} style={{ width: '100%', fontSize: '0.9rem' }}>
                        Level Up Profession
                      </button>
                    </div>
                  )}
                </div>

                {/* Right: Worktable */}
                <div className={styles.worktableContainer}>
                  {renderWorktable()}
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.3 }}>⚒️</div>
                <h2>Select a Profession</h2>
                <p>Choose a discipline from the sidebar to begin your work.</p>
                <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', maxWidth: '400px', fontSize: '0.9rem', lineHeight: '1.4' }}>
                  <p>Starting at level 6, and every 3 levels thereafter (6, 9, 12, 15, 18...), you earn profession points. You can spend these in any profession you choose. Blueprints must be studied before they can be crafted.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CraftingDistrict;
